import React from 'react';
import { CheckCircle2, XCircle, MessageSquare, FileText, Download, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * Composant Actions Client
 * Version simplifiée pour les clients avec actions limitées
 */

const ActionsClient = ({
  operation,
  onValidateQuote,
  onDownloadQuote,
  onDownloadInvoice,
  onViewDetails
}) => {
  
  // Déterminer l'action principale selon le statut
  const getPrimaryAction = () => {
    switch (operation.status) {
      case 'en_attente_validation_client':
        return {
          label: 'Valider / Refuser / Négocier',
          description: 'Vous devez prendre une décision sur le devis proposé',
          icon: <CheckCircle2 className="w-5 h-5" />,
          color: 'bg-emerald-600 hover:bg-emerald-700',
          action: onValidateQuote
        };
      
      case 'en_attente_paiement':
        return {
          label: 'Télécharger la facture',
          description: 'Votre facture est disponible',
          icon: <Download className="w-5 h-5" />,
          color: 'bg-orange-600 hover:bg-orange-700',
          action: onDownloadInvoice
        };
      
      default:
        return null;
    }
  };

  const primaryAction = getPrimaryAction();

  // Actions secondaires disponibles pour le client
  const getSecondaryActions = () => {
    const actions = [];

    // Télécharger devis (si disponible)
    if (['en_attente_validation_client', 'validee_planification', 'planifiee', 'en_cours_de_chargement', 'charge_en_route', 'livree', 'en_attente_paiement', 'payee', 'terminee'].includes(operation.status)) {
      actions.push({
        label: 'Télécharger le devis',
        icon: <FileText className="w-4 h-4" />,
        action: onDownloadQuote
      });
    }

    // Télécharger facture (si disponible)
    // La facture est disponible si: 
    // 1. Le statut logistique indique qu'elle est générée, OU
    // 2. Le statut financier est 'invoiced', 'pending_payment', 'partially_paid' ou 'paid', OU
    // 3. Un numéro de facture existe
    const financialStatus = operation.financialStatus || operation.financial_status;
    const hasInvoice = 
      ['en_attente_paiement', 'payee', 'terminee', 'facturee'].includes(operation.status) ||
      ['invoiced', 'pending_payment', 'partially_paid', 'paid'].includes(financialStatus) ||
      operation.invoiceNumber;
    
    if (hasInvoice) {
      actions.push({
        label: 'Télécharger la facture',
        icon: <FileText className="w-4 h-4" />,
        action: onDownloadInvoice
      });
    }

    // Télécharger CMR (si livré)
    // → Désactivé : remplacé par les bons de chargement / déchargement signés
    // (les CMR ne sont plus utilisés par Baobab Connect)

    return actions;
  };

  const secondaryActions = getSecondaryActions();

  // Messages contextuels selon le statut
  const getStatusMessage = () => {
    switch (operation.status) {
      case 'demande_enregistree':
      case 'en_attente_de_devis':
        return {
          type: 'info',
          message: '⏳ Votre demande est en cours de traitement. Vous serez notifié dès que le devis sera prêt.'
        };
      
      case 'devis_cree':
      case 'en_attente_validation_client':
        return {
          type: 'warning',
          message: '🎯 Action requise : Veuillez consulter et valider votre devis pour que nous puissions planifier votre opération.'
        };
      
      case 'en_negociation':
        return {
          type: 'info',
          message: '💬 Votre proposition est en cours d\'analyse. Nous reviendrons vers vous rapidement.'
        };
      
      case 'devis_accepte':
      case 'planifiee':
        return {
          type: 'success',
          message: '✅ Votre opération est planifiée. Nous vous tiendrons informé de son avancement.'
        };
      
      case 'en_cours_de_chargement':
        return {
          type: 'info',
          message: '📦 Le chargement de votre marchandise est en cours.'
        };
      
      case 'charge_en_route':
        return {
          type: 'info',
          message: '🚛 Votre marchandise est en route vers sa destination.'
        };
      
      case 'livree':
        return {
          type: 'success',
          message: '✅ Votre marchandise a été livrée avec succès !'
        };
      
      case 'en_attente_paiement':
        return {
          type: 'warning',
          message: '💳 Votre facture est disponible. Merci de procéder au paiement.'
        };
      
      case 'payee':
        return {
          type: 'success',
          message: '✅ Paiement reçu. Merci pour votre confiance !'
        };
      
      case 'terminee':
        return {
          type: 'success',
          message: '🎉 Opération terminée avec succès. Merci de votre confiance !'
        };
      
      case 'annulee_par_client':
        return {
          type: 'error',
          message: '❌ Cette opération a été annulée à votre demande.'
        };
      
      case 'annulee_admin':
        return {
          type: 'error',
          message: '❌ Cette opération a été annulée. Contactez-nous pour plus d\'informations.'
        };
      
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-blue-900 text-base">
          <FileText className="w-4 h-4" />
          Vos Actions
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-3 pt-0">
        {/* Message de statut */}
        {statusMessage && (
          <div className={`rounded-lg p-3 border ${
            statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
            statusMessage.type === 'warning' ? 'bg-amber-50 border-amber-200' :
            statusMessage.type === 'error' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <p className={`text-xs leading-relaxed ${
              statusMessage.type === 'success' ? 'text-emerald-800' :
              statusMessage.type === 'warning' ? 'text-amber-800' :
              statusMessage.type === 'error' ? 'text-red-800' :
              'text-blue-800'
            }`}>
              {statusMessage.message}
            </p>
          </div>
        )}

        {/* Action Principale */}
        {primaryAction && (
          <Button
            className={`w-full h-auto py-3 text-base font-semibold text-white shadow-md ${primaryAction.color}`}
            onClick={primaryAction.action}
          >
            {primaryAction.icon}
            <span className="ml-2">{primaryAction.label}</span>
          </Button>
        )}

        {/* Actions Secondaires */}
        {secondaryActions.length > 0 && (
          <div className="space-y-2">
            {secondaryActions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="w-full bg-white hover:bg-gray-50 border-gray-200 justify-start"
                onClick={action.action}
              >
                {action.icon}
                <span className="ml-2 text-sm">{action.label}</span>
              </Button>
            ))}
          </div>
        )}

        {/* Info supplémentaire */}
        <div className="pt-2 border-t border-blue-200">
          <p className="text-xs text-gray-600 text-center">
            💡 Besoin d'aide ? Contactez votre opérateur
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActionsClient;